import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, PLATFORM_ID, Inject } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Subscription } from 'rxjs';
import { GetProductPageQuery, CustomizableAssetInput } from '../../../common/generated-types';
import { CartService } from '../../../core/providers/cart/cart.service';
import { AddToCartResult, MobileBuyBannerType } from '../../../common/enums';
import { ProductCustomizationMessageOption, ProductCustomizationOption, ProductCustomizationPaidOption, ProductCustomizationPrintTemplate, ProductCustomizationTextFramesData } from '../../../common/interfaces';
import { ModalService } from '../../../core/providers/modal/modal.service';
import { ImageCustomizerModalComponent } from '../image-customizer-modal/image-customizer-modal.component';
import { notNullOrUndefined } from '../../../common/utils/not-null-or-undefined';
import { SeparatedFormatPricePipe } from '../../pipes/separated-format-price.pip';
import { StateService } from '../../../core/providers/state/state.service';
import { ActivatedRoute } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { take } from 'rxjs/operators';

type Variant = NonNullable<GetProductPageQuery['productPage']['productInfo']>['variants'][number];

@Component({
    selector: 'vsf-product-shop-now-banner',
    templateUrl: './product-shop-now-banner.component.html',
    styleUrls: ['./product-shop-now-banner.component.scss'],
})
export class ProductShopNowBannerComponent implements OnInit, OnChanges, OnDestroy {
    @Input() product: GetProductPageQuery['productPage']['productInfo'];
    @Input() selectedVariant: Variant;
    @Input() selectedPrintTemplate: ProductCustomizationOption | null = null;
    @Input() inStock = true;
    @Input() isMobile: boolean;
    @Input() mobileBuyBannerType: MobileBuyBannerType = MobileBuyBannerType.Regular;
    @Input() isFloatingBanner: boolean = false;
    @Input() messageValues: { [optionId: string]: string } = {};
    @Input() selectedPaidOptionValues: { [optionId: string]: string } = {};
    @Input() allCustomizationOptions: ProductCustomizationOption[] = [];
    isStorePublished = true;
    inFlight = false;
    selectedVariantMSRP: number | null = null;
    discountPercentage: number | null = null;
    buttonText: string = 'Shop Now';
    totalDisplayPrice: number = 0;
    customizeOrder: boolean = false;
    hasDirectCustomOptions: boolean = false;
    messageOptions: ProductCustomizationMessageOption[] = [];
    paidOptions: ProductCustomizationPaidOption[] = [];
    textFrameOptions: { optionId: string; data: ProductCustomizationTextFramesData }[] = [];
    customizationOptions: ProductCustomizationOption[] = [];
    private subscription = new Subscription();
    private shouldAutoOpenFromUrl = false;
    private hasAutoOpenedFromUrl = false;

    constructor(
        @Inject(PLATFORM_ID) private platformId: object,
        private changeDetector: ChangeDetectorRef,
        private modalService: ModalService,
        private cartService: CartService,
        private stateService: StateService,
        private route: ActivatedRoute,
        private logger: NGXLogger) { }

    ngOnInit() {
        this.logger.debug(`[selectedVariant]${this.selectedVariant?.name}`);
        this.refreshVariantState();
        this.readAutoOpenFlagFromUrl();
        this.subscription.add(
            this.stateService.select(state => state.storeStatus).subscribe(status => {
                this.isStorePublished = status === 'published';
                this.changeDetector.markForCheck();
            })
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['selectedVariant'] || changes['allCustomizationOptions']) {
            this.refreshVariantState();
            this.tryAutoOpenCustomizer();
        }
        if (changes['selectedPrintTemplate']) {
            this.refreshVariantState();
            this.tryAutoOpenCustomizer();
        }
        if (changes['messageValues']) {
            this.changeDetector.markForCheck();
        }
        if (changes['selectedPaidOptionValues']) {
            this.recomputeButtonText();
        }
    }

    private refreshVariantState(): void {
        this.updateCustomizationOptions();

        const directOptions = this.allCustomizationOptions.filter(
            o => o.type === 'custom-message' || o.type === 'custom-options'
        );
        this.hasDirectCustomOptions = directOptions.length > 0 && !this.selectedPrintTemplate;

        this.totalDisplayPrice = this.selectedVariant?.price ?? 0;

        if (!this.inStock) {
          this.buttonText     = 'Sold Out';
          this.customizeOrder = false;
          return;
        }
        if (this.selectedPrintTemplate) {
          this.buttonText     = 'Customize Now';
          this.customizeOrder = true;
        } else {
          this.customizeOrder = false;
          this.recomputeButtonText();
        }

        if (this.selectedVariant && this.selectedVariant.customFields?.MSRP && this.selectedVariant.customFields.MSRP !== 0) {
            this.selectedVariantMSRP = this.selectedVariant.customFields.MSRP;
            const price = this.selectedVariant.price;
            const msrp = this.selectedVariantMSRP;
            if (msrp && msrp > price) {
                this.discountPercentage = ((msrp - price) / msrp) * 100;
            } else {
                this.discountPercentage = null;
            }
        } else {
            this.selectedVariantMSRP = null;
            this.discountPercentage = null;
        }
        this.changeDetector.markForCheck();
    }

    private updateCustomizationOptions() {
        const customizationOptions: ProductCustomizationOption[] = [];
        type VariantCustomizationOptionData = NonNullable<NonNullable<NonNullable<GetProductPageQuery['productPage']['productInfo']>['variants'][number]>['customizationOptions']>[number];
        this.selectedVariant?.customizationOptions?.forEach((option: VariantCustomizationOptionData) => {
            customizationOptions.push(option as any as ProductCustomizationOption);
        });
        type ProductCustomizationOptionData = NonNullable<NonNullable<GetProductPageQuery['productPage']['productInfo']>['customizationOptions']>[number];
        this.product?.customizationOptions?.forEach((productOption: ProductCustomizationOptionData) => {
            if(customizationOptions.find((option: ProductCustomizationOption) => option.id === productOption.id)) {
                return;
            }
            customizationOptions.push(productOption as any as ProductCustomizationOption);
        });
        this.customizationOptions = customizationOptions;
        this.messageOptions = this.customizationOptions.map(
            (option: ProductCustomizationOption) =>
                option.type === 'custom-message' ?
                    {
                        optionId: option.id ?? '',
                        ...option.message,

                    } as ProductCustomizationMessageOption : null).filter(notNullOrUndefined);
        this.paidOptions = this.customizationOptions.map(
            (option: ProductCustomizationOption) =>
                option.type === 'custom-options' ?
                    {
                        optionId: option.id ?? '',
                        ...option.paidOption
                    } as ProductCustomizationPaidOption : null).filter(notNullOrUndefined);
        if (this.selectedPrintTemplate) {
            this.textFrameOptions = this.customizationOptions
                .filter((o): o is ProductCustomizationOption & { textFrames: ProductCustomizationTextFramesData } =>
                    o.type === 'custom-text-frames' && !!o.textFrames)
                .map(o => ({ optionId: o.id, data: o.textFrames }));
        } else {
            const hasTextFrames = this.customizationOptions.some(o => o.type === 'custom-text-frames' && !!o.textFrames);
            if (hasTextFrames) {
                this.logger.warn('Text-frame options found but no print template is assigned — text frames will be ignored');
            }
            this.textFrameOptions = [];
        }
    }

    private readAutoOpenFlagFromUrl() {
        if (!isPlatformBrowser(this.platformId)) {
          return;
        }

        this.route.queryParamMap
          .pipe(take(1))
          .subscribe(params => {
            const customizeParam = params.get('customize');
            this.shouldAutoOpenFromUrl =
              customizeParam === '1' || customizeParam === 'true';
            this.tryAutoOpenCustomizer();
          });
    }

    private tryAutoOpenCustomizer() {
        if (!isPlatformBrowser(this.platformId)) return;
        if (!this.shouldAutoOpenFromUrl || this.hasAutoOpenedFromUrl || this.isFloatingBanner) return;

        if (!this.selectedVariant || !this.selectedPrintTemplate) {
          this.logger.debug('Auto-open skipped: selectedVariant or selectedPrintTemplate not ready');
          return;
        }

        this.hasAutoOpenedFromUrl = true;

        setTimeout(() => {
          this.logger.debug('Auto-opening customization modal from URL...');
          this.addToCartAndOpenDrawer();
        }, 0);
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    addToCartAndOpenDrawer() {
        this.inFlight = true;
        if(this.customizeOrder) {
            // Print template requires the image customizer modal
            this.modalService.fromComponent(ImageCustomizerModalComponent, {
                size: 'xl',
                locals: {
                    title: 'Customize',
                    printingPattern: {
                        optionId: this.selectedPrintTemplate?.id ?? '',
                        ...this.selectedPrintTemplate?.printTemplate
                    } as ProductCustomizationPrintTemplate,
                    messageOptions: this.messageOptions && this.messageOptions.length > 0 ? this.messageOptions : [],
                    paidOptions: this.paidOptions && this.paidOptions.length > 0 ? this.paidOptions : [],
                    textFrameOptions: this.textFrameOptions,
                    customizationOptions: this.customizationOptions,
                    productVariantId: this.selectedVariant?.id,
                    variantPrice: this.selectedVariant?.price ?? 0,
                    variantName: this.selectedVariant?.name,
                    isMobile: this.isMobile,
                },
                closable: true,
            }).subscribe((result: CustomizableAssetInput[] | undefined) => {
                if(!result) {
                    this.inFlight = false;
                    this.changeDetector.markForCheck();
                    return;
                }
                this.logger.debug('ImageEditorModal result', result);
                this.subscription.add(
                    this.cartService.addToCartAndOpenDrawer(
                        this.selectedVariant?.id,
                        1,
                        result
                    ).subscribe({
                        next: (result: AddToCartResult) => {
                            this.inFlight = false;
                            if (result === AddToCartResult.OUT_OF_STOCK) {
                                this.inStock = false;
                            }
                            this.changeDetector.markForCheck();
                        },
                        error: err => {
                        this.inFlight = false;
                        this.logger.error('Error in addToCartAndOpenDrawer', err);
                        this.changeDetector.markForCheck();
                        },
                    }),
                );

            });
        } else if (this.hasDirectCustomOptions) {
            // Build customizable assets from PDP selections (message + paid options)
            const assets = this.buildDirectCustomizationAssets();
            this.changeDetector.markForCheck();
            this.subscription.add(
                this.cartService.addToCartAndOpenDrawer(this.selectedVariant?.id, 1, assets).subscribe({
                    next: (result: AddToCartResult) => {
                        this.inFlight = false;
                        if (result === AddToCartResult.OUT_OF_STOCK) {
                            this.inStock = false;
                        }
                        this.changeDetector.markForCheck();
                    },
                    error: err => {
                        this.inFlight = false;
                        this.logger.error('Error in addToCartAndOpenDrawer', err);
                        this.changeDetector.markForCheck();
                    },
                }),
            );
        } else {
            this.changeDetector.markForCheck();

            this.subscription.add(
                this.cartService.addToCartAndOpenDrawer(this.selectedVariant?.id).subscribe({
                    next: (result: AddToCartResult) => {
                        this.inFlight = false;
                        if (result === AddToCartResult.OUT_OF_STOCK) {
                        this.inStock = false;
                        }
                        this.changeDetector.markForCheck();
                    },
                    error: err => {
                    this.inFlight = false;
                    this.logger.error('Error in addToCartAndOpenDrawer', err);
                    this.changeDetector.markForCheck();
                    },
                }),
            );
        }
    }

    isAddToCartDisabled(): boolean {
        if (this.inFlight || !this.inStock) {
            return true;
        }
        if (this.hasDirectCustomOptions) {
            for (const option of this.allCustomizationOptions) {
                if (option.type === 'custom-message' && option.message?.required) {
                    if (!this.messageValues[option.id]?.trim()) {
                        return true;
                    }
                }
                if (option.type === 'custom-options' && option.paidOption?.required) {
                    if (!this.selectedPaidOptionValues[option.id]) {
                        return true;
                    }
                }
            }
        }
        return false;
    }

    private getPaidOptionAddOnTotal(): number {
        let total = 0;
        for (const option of this.allCustomizationOptions) {
            if (option.type === 'custom-options' && option.paidOption) {
                const selectedKey = this.selectedPaidOptionValues[option.id];
                if (selectedKey) {
                    const item = option.paidOption.options?.find(o => o.key === selectedKey);
                    total += item?.price ?? 0;
                }
            }
        }
        return total;
    }

    private recomputeButtonText(): void {
        if (!this.inStock) return;
        if (this.selectedPrintTemplate) return;
        const addOn = this.getPaidOptionAddOnTotal();
        this.totalDisplayPrice = (this.selectedVariant?.price ?? 0) + addOn;
        if (this.isMobile) {
            this.buttonText = 'Shop Now';
        } else {
            const currencyPipe = new SeparatedFormatPricePipe(this.changeDetector, this.stateService);
            const formattedPrice = currencyPipe.transform(this.totalDisplayPrice);
            this.buttonText = `${formattedPrice?.currency}${formattedPrice?.price} Shop Now`;
        }
        this.changeDetector.markForCheck();
    }

    private buildDirectCustomizationAssets(): CustomizableAssetInput[] {
        const assets: CustomizableAssetInput[] = [];
        for (const option of this.allCustomizationOptions) {
            if (option.type === 'custom-message' && option.message) {
                const text = this.messageValues[option.id] || '';
                assets.push({
                    name: option.name,
                    type: 'text',
                    text,
                    productCustomizationOptionId: option.id,
                    config: {
                        label: option.message.label,
                        content: text,
                        required: option.message.required ?? false,
                    },
                });
            } else if (option.type === 'custom-options' && option.paidOption) {
                const selectedKey = this.selectedPaidOptionValues[option.id];
                if (selectedKey) {
                    const selectedItem = option.paidOption.options?.find(o => o.key === selectedKey);
                    assets.push({
                        name: option.name,
                        type: 'paid-option',
                        productCustomizationOptionId: option.id,
                        config: {
                            label: option.name,
                            content: selectedItem?.label ?? selectedKey,
                            key: selectedKey,
                            required: option.paidOption.required ?? false,
                        },
                        amount: selectedItem?.price ?? 0,
                    });
                }
            }
        }
        return assets;
    }
}
